import React, { createContext, useState, useContext } from 'react';

const CartContext = createContext();

// Products without a `stock` field are treated as unlimited.
export const stockLimit = (product) =>
  typeof product?.stock === 'number' ? product.stock : Infinity;

export const isOutOfStock = (product) => stockLimit(product) < 1;

// Create a custom hook to use the Cart Context
export const useCart = () => {
  return useContext(CartContext);
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  // Function to add an item to the cart
  const addToCart = (product) => {
    const limit = stockLimit(product);
    if (limit < 1) return;

    const existingItemIndex = cartItems.findIndex((item) => item.id === product.id);

    if (existingItemIndex !== -1) {
      const updatedCartItems = cartItems.map((item, index) =>
        index === existingItemIndex
          ? { ...item, quantity: Math.min(item.quantity + 1, limit) }
          : item
      );
      setCartItems(updatedCartItems);
    } else {
      setCartItems([...cartItems, { ...product, quantity: 1 }]);
    }
  };

  // Function to update the quantity of an item in the cart
  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity > 0) {
      const updatedCartItems = cartItems.map((item) =>
        item.id === itemId
          ? { ...item, quantity: Math.min(parseInt(newQuantity, 10), stockLimit(item)) }
          : item
      );
      setCartItems(updatedCartItems);
    } else {
      removeFromCart(itemId);
    }
  };

  // Function to remove an item from the cart
  const removeFromCart = (itemId) => {
    const updatedCartItems = cartItems.filter((item) => item.id !== itemId);
    setCartItems(updatedCartItems);
  };

  // Function to calculate the total price of items in the cart
  const calculateTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        updateQuantity,
        removeFromCart,
        calculateTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};